import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  checkServerStatus,
  uploadImage,
  executeWorkflow,
  checkWorkflowStatus,
  fetchWorkflowResults
} from '../services/itoraApi';
import { useWebSocket } from '../hooks/useWebSocket';
import './PatternSwap.css';

const WORKFLOW_FILE = 'KNIX-PATTERN_FLIP-r1.json';

const maskBackendName = (text = '') => String(text).replace(/comfyui/gi, 'processing engine');

/**
 * Canvas constants
 */
const CANVAS_W = 1792;
const CANVAS_H = 1792;
const BASE_TILE = 256;

/**
 * Zoomable image viewer — scroll to zoom, drag to pan
 */
const ZoomableImage = ({ src, alt, showClear, onClear }) => {
  const containerRef = useRef(null);
  const zoomRef = useRef(1);
  const panRef = useRef({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const panStartRef = useRef({ x: 0, y: 0, px: 0, py: 0 });

  // Non-passive wheel listener to block page scroll
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onWheel = (e) => {
      e.preventDefault();
      e.stopPropagation();
      const rect = el.getBoundingClientRect();
      const cx = e.clientX - rect.left - rect.width / 2;
      const cy = e.clientY - rect.top - rect.height / 2;
      const delta = e.deltaY > 0 ? -0.08 : 0.08;
      const z = zoomRef.current;
      const nz = Math.max(0.5, Math.min(8, z + delta));
      const scaleChange = nz / z;
      zoomRef.current = nz;
      const p = panRef.current;
      panRef.current = {
        x: p.x - (cx * (scaleChange - 1)),
        y: p.y - (cy * (scaleChange - 1)),
      };
      setZoom(nz);
      setPan(panRef.current);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  const handlePointerDown = useCallback((e) => {
    if (zoom <= 1) return;
    setIsPanning(true);
    panStartRef.current = {
      x: e.clientX ?? e.touches?.[0]?.clientX ?? 0,
      y: e.clientY ?? e.touches?.[0]?.clientY ?? 0,
      px: panRef.current.x,
      py: panRef.current.y,
    };
    e.preventDefault();
  }, [zoom]);

  const handlePointerMove = useCallback((e) => {
    if (!isPanning) return;
    const cx = e.clientX ?? e.touches?.[0]?.clientX ?? 0;
    const cy = e.clientY ?? e.touches?.[0]?.clientY ?? 0;
    const nextPan = {
      x: panStartRef.current.px + (cx - panStartRef.current.x),
      y: panStartRef.current.py + (cy - panStartRef.current.y),
    };
    panRef.current = nextPan;
    setPan(nextPan);
  }, [isPanning]);

  const handlePointerUp = useCallback(() => setIsPanning(false), []);

  const resetView = () => {
    zoomRef.current = 1;
    panRef.current = { x: 0, y: 0 };
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };

  return (
    <div
      ref={containerRef}
      className={`ps-zoom-container${zoom > 1 ? ' zoomed' : ''}${isPanning ? ' panning' : ''}`}
      onMouseDown={handlePointerDown}
      onMouseMove={handlePointerMove}
      onMouseUp={handlePointerUp}
      onMouseLeave={handlePointerUp}
      onTouchStart={handlePointerDown}
      onTouchMove={handlePointerMove}
      onTouchEnd={handlePointerUp}
    >
      <div
        className="ps-zoom-inner"
        style={{
          transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
        }}
      >
        <img src={src} alt={alt} className="ps-zoom-img" />
      </div>
      <div className="ps-zoom-controls">
        <span className="ps-zoom-level">{Math.round(zoom * 100)}%</span>
        {zoom > 1 && <button className="ps-btn-sm ps-zoom-reset" onClick={resetView}>Reset</button>}
        {showClear && <button className="ps-btn-sm ps-zoom-clear" onClick={onClear}>✕</button>}
      </div>
    </div>
  );
};

/**
 * Comparison slider component — hover to compare before/after, scroll to zoom
 */
const ComparisonSlider = ({ beforeSrc, afterSrc, beforeLabel, afterLabel }) => {
  const containerRef = useRef(null);
  const zoomRef = useRef(1);
  const panRef = useRef({ x: 0, y: 0 });
  const [pos, setPos] = useState(50);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [panning, setPanning] = useState(false);
  const isPanningRef = useRef(false);
  const panDragRef = useRef({ x: 0, y: 0, px: 0, py: 0 });

  const handleMove = useCallback((e) => {
    // Keep slider strictly hover-driven; never update while mouse is down / panning.
    const mouseButtonDown = typeof e.buttons === 'number' && e.buttons > 0;
    if (isPanningRef.current || panning || mouseButtonDown) return;
    if (!containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const clientX = e.clientX ?? e.touches?.[0]?.clientX ?? 0;

    // Convert pointer X (screen space) back into the untransformed image space,
    // so the divider stays aligned with the cursor at any zoom/pan level.
    const xOnContainer = clientX - rect.left;
    const centerX = rect.width / 2;
    const xInImageSpace = ((xOnContainer - centerX - pan.x) / zoom) + centerX;

    const pct = Math.max(2, Math.min(98, (xInImageSpace / rect.width) * 100));
    setPos(pct);
  }, [panning, pan.x, zoom]);

  // Non-passive wheel listener to block page scroll
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onWheel = (e) => {
      e.preventDefault();
      e.stopPropagation();
      const rect = el.getBoundingClientRect();
      const cx = e.clientX - rect.left - rect.width / 2;
      const cy = e.clientY - rect.top - rect.height / 2;
      const delta = e.deltaY > 0 ? -0.08 : 0.08;
      const z = zoomRef.current;
      const nz = Math.max(0.5, Math.min(8, z + delta));
      const scaleChange = nz / z;
      zoomRef.current = nz;
      const p = panRef.current;
      panRef.current = {
        x: p.x - (cx * (scaleChange - 1)),
        y: p.y - (cy * (scaleChange - 1)),
      };
      setZoom(nz);
      setPan(panRef.current);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  // Pan drag handlers (left mouse button when zoomed)
  const handlePanDown = useCallback((e) => {
    if (typeof e.button === 'number' && e.button !== 0) return; // only left button
    isPanningRef.current = true;
    setPanning(true);
    panDragRef.current = {
      x: e.clientX ?? e.touches?.[0]?.clientX ?? 0,
      y: e.clientY ?? e.touches?.[0]?.clientY ?? 0,
      px: panRef.current.x,
      py: panRef.current.y,
    };
    e.stopPropagation();
    e.preventDefault();
  }, []);

  const handlePanMove = useCallback((e) => {
    if (!panning) return;
    const cx = e.clientX ?? e.touches?.[0]?.clientX ?? 0;
    const cy = e.clientY ?? e.touches?.[0]?.clientY ?? 0;
    const p = panDragRef.current;
    panRef.current = {
      x: p.px + (cx - p.x),
      y: p.py + (cy - p.y),
    };
    setPan(panRef.current);
  }, [panning]);

  const handlePanUp = useCallback(() => {
    isPanningRef.current = false;
    setPanning(false);
  }, []);

  const resetZoom = () => {
    zoomRef.current = 1;
    panRef.current = { x: 0, y: 0 };
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };

  return (
    <div
      ref={containerRef}
      className={`ps-compare${zoom > 1 ? ' zoomed' : ''}${panning ? ' panning' : ''}`}
      onMouseMove={handleMove}
      onTouchMove={handleMove}
      onMouseDown={handlePanDown}
      onMouseMoveCapture={handlePanMove}
      onMouseUp={handlePanUp}
      onMouseLeave={handlePanUp}
    >
      <div
        className="ps-compare-inner"
        style={{
          transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
        }}
      >
        {/* After (full width, underneath) */}
        <img src={afterSrc} alt={afterLabel} className="ps-compare-after" />
        {/* Before (clipped) */}
        <div className="ps-compare-before" style={{ clipPath: `inset(0 0 0 ${pos}%)` }}>
          <img src={beforeSrc} alt={beforeLabel} />
        </div>
        {/* Slider line — inside scaled inner so it moves with the image */}
        <div className="ps-compare-line-inner" style={{ left: `${pos}%` }}>
          <div className="ps-compare-handle" />
        </div>
      </div>
      {/* Labels */}
      <span className="ps-compare-label ps-label-before">{beforeLabel}</span>
      <span className="ps-compare-label ps-label-after">{afterLabel}</span>
      {/* Zoom controls */}
      <div className="ps-zoom-controls">
        <span className="ps-zoom-level">{Math.round(zoom * 100)}%</span>
        {zoom > 1 && <button className="ps-btn-sm ps-zoom-reset" onClick={resetZoom}>Reset</button>}
      </div>
    </div>
  );
};

const PatternSwap = () => {
  // ── Server & connection ──────────────────────────────────────────
  const [serverStatus, setServerStatus] = useState('checking');
  const [wsConnected, setWsConnected] = useState(false);

  // ── Image inputs ─────────────────────────────────────────────────
  const [targetImage, setTargetImage] = useState(null);
  const [targetPreview, setTargetPreview] = useState(null);
  const [patternImage, setPatternImage] = useState(null);
  const [patternPreview, setPatternPreview] = useState(null);
  const [isTargetDragOver, setIsTargetDragOver] = useState(false);

  // ── Pattern controls ─────────────────────────────────────────────
  const [repeatCount, setRepeatCount] = useState(1.0);
  const [offsetX, setOffsetX] = useState(0);
  const [offsetY, setOffsetY] = useState(0);

  // ── Canvas refs ──────────────────────────────────────────────────
  const hiddenCanvasRef = useRef(null);
  const previewCanvasRef = useRef(null);
  const patternCanvasWrapRef = useRef(null);
  const [tiledPreviewUrl, setTiledPreviewUrl] = useState(null);
  const [patternImgEl, setPatternImgEl] = useState(null);

  // ── Drag state ───────────────────────────────────────────────────
  const dragRef = useRef({ active: false, startX: 0, startY: 0, offX: 0, offY: 0 });
  const targetDragDepthRef = useRef(0);

  // ── Workflow execution ───────────────────────────────────────────
  const [workflow, setWorkflow] = useState(null);
  const [isRunning, setIsRunning] = useState(false);
  const [progress, setProgress] = useState('Ready');
  const [progressPct, setProgressPct] = useState(0);
  const [logs, setLogs] = useState([]);
  const [resultImage, setResultImage] = useState(null);
  const [sessionHistory, setSessionHistory] = useState([]);

  // ── WebSocket ────────────────────────────────────────────────────
  const addLog = useCallback((msg) => {
    setLogs(prev => [...prev, { ts: new Date().toLocaleTimeString(), msg: maskBackendName(msg) }]);
  }, []);

  const handleWsMessage = useCallback((message) => {
    switch (message.type) {
      case 'execution_start':
        setProgress('Starting execution…');
        setProgressPct(0);
        break;
      case 'executing': {
        const ep = message.data?.progress;
        if (ep?.value !== undefined && ep?.max !== undefined) {
          const pct = Math.round((ep.value / ep.max) * 100);
          setProgressPct(pct);
          setProgress(`Processing — ${pct}%`);
        }
        break;
      }
      case 'executed':
        setProgress('Nodes complete');
        setProgressPct(90);
        break;
      case 'execution_error':
        setProgress('Execution failed');
        setIsRunning(false);
        break;
      default:
        break;
    }
  }, []);

  const handleStatusChange = useCallback((status) => {
    setWsConnected(status === 'connected' || status === 'comfyui_connected');
  }, []);

  const { sendMessage } = useWebSocket(handleWsMessage, handleStatusChange);

  // ── Initialise ───────────────────────────────────────────────────
  useEffect(() => { init(); }, []);

  const init = async () => {
    try {
      const status = await checkServerStatus();
      setServerStatus(status.status);
      addLog(`Server: ${status.status}`);
    } catch (e) {
      setServerStatus('error');
      addLog(`Server check error: ${e.message}`);
    }
    try {
      const resp = await fetch(`/itoraWorkflow/${WORKFLOW_FILE}`);
      if (resp.ok) {
        const wf = await resp.json();
        setWorkflow(wf);
        addLog(`Workflow loaded: ${WORKFLOW_FILE} (${Object.keys(wf).length} nodes)`);
      } else {
        addLog(`Workflow file not found: ${WORKFLOW_FILE}`);
      }
    } catch (e) {
      addLog(`Workflow load error: ${e.message}`);
    }
  };

  // ── File handlers ────────────────────────────────────────────────
  const handleTargetFile = (file) => {
    if (!file) return;
    setTargetImage(file);
    setTargetPreview(URL.createObjectURL(file));
    setResultImage(null);
    addLog(`TARGET selected: ${file.name}`);
  };

  const handlePatternFile = (file) => {
    if (!file) return;
    setPatternImage(file);
    const url = URL.createObjectURL(file);
    setPatternPreview(url);
    setResultImage(null);
    const img = new Image();
    img.onload = () => setPatternImgEl(img);
    img.src = url;
    addLog(`PATTERN selected: ${file.name}`);
  };

  const hasFileDrag = (e) => Array.from(e.dataTransfer?.types || []).includes('Files');

  const handleTargetDragEnter = (e) => {
    if (!hasFileDrag(e)) return;
    e.preventDefault();
    e.stopPropagation();
    targetDragDepthRef.current += 1;
    setIsTargetDragOver(true);
  };

  const handleTargetDragOver = (e) => {
    if (!hasFileDrag(e)) return;
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
    if (!isTargetDragOver) setIsTargetDragOver(true);
  };

  const handleTargetDragLeave = (e) => {
    if (!hasFileDrag(e)) return;
    e.preventDefault();
    e.stopPropagation();
    targetDragDepthRef.current = Math.max(0, targetDragDepthRef.current - 1);
    if (targetDragDepthRef.current === 0) setIsTargetDragOver(false);
  };

  const handleTargetDrop = (e) => {
    if (!hasFileDrag(e)) return;
    e.preventDefault();
    e.stopPropagation();
    targetDragDepthRef.current = 0;
    setIsTargetDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) handleTargetFile(file);
  };

  // ── Draw tiled pattern onto a canvas ─────────────────────────────
  const drawTiled = useCallback((canvas, img, pct, ox, oy) => {
    const ctx = canvas.getContext('2d');
    canvas.width = CANVAS_W;
    canvas.height = CANVAS_H;
    ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);

    const tileSize = CANVAS_W / pct;
    const cols = Math.ceil(CANVAS_W / tileSize) + 2;
    const rows = Math.ceil(CANVAS_H / tileSize) + 2;

    for (let r = -1; r < rows; r++) {
      for (let c = -1; c < cols; c++) {
        ctx.drawImage(
          img,
          c * tileSize + ox,
          r * tileSize + oy,
          tileSize,
          tileSize
        );
      }
    }
  }, []);

  // ── Re-render on control change ──────────────────────────────────
  useEffect(() => {
    if (!patternImgEl || !hiddenCanvasRef.current) return;

    drawTiled(hiddenCanvasRef.current, patternImgEl, repeatCount, offsetX, offsetY);
    const url = hiddenCanvasRef.current.toDataURL('image/png');
    setTiledPreviewUrl(url);

    if (previewCanvasRef.current) {
      drawTiled(previewCanvasRef.current, patternImgEl, repeatCount, offsetX, offsetY);
    }
  }, [patternImgEl, repeatCount, offsetX, offsetY, drawTiled]);

  // ── Drag handlers on the preview canvas ──────────────────────────
  const handlePointerDown = useCallback((e) => {
    const rect = previewCanvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    dragRef.current = {
      active: true,
      startX: e.clientX ?? e.touches?.[0]?.clientX ?? 0,
      startY: e.clientY ?? e.touches?.[0]?.clientY ?? 0,
      offX: offsetX,
      offY: offsetY,
    };
    e.preventDefault();
  }, [offsetX, offsetY]);

  // ── Scroll to zoom pattern ───────────────────────────────────────
  useEffect(() => {
    const el = patternCanvasWrapRef.current;
    if (!el) return;
    const onWheel = (e) => {
      e.preventDefault();
      e.stopPropagation();
      const delta = e.deltaY > 0 ? 0.05 : -0.05;
      setRepeatCount(prev => Math.max(0.1, Math.min(3, Number((prev + delta).toFixed(2)))));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  const handlePointerMove = useCallback((e) => {
    if (!dragRef.current.active) return;
    const cx = e.clientX ?? e.touches?.[0]?.clientX ?? 0;
    const cy = e.clientY ?? e.touches?.[0]?.clientY ?? 0;
    const DRAG_SENSITIVITY = 2;
    const dx = (cx - dragRef.current.startX) * DRAG_SENSITIVITY;
    const dy = (cy - dragRef.current.startY) * DRAG_SENSITIVITY;

    const tileSize = CANVAS_W / repeatCount;
    const clampedX = Math.max(-tileSize, Math.min(tileSize, dragRef.current.offX + dx));
    const clampedY = Math.max(-tileSize, Math.min(tileSize, dragRef.current.offY + dy));

    setOffsetX(clampedX);
    setOffsetY(clampedY);
    e.preventDefault();
  }, [repeatCount]);

  const handlePointerUp = useCallback(() => {
    dragRef.current.active = false;
  }, []);

  // ── data-URL → File ──────────────────────────────────────────────
  const dataUrlToFile = (dataUrl, name) => {
    const parts = dataUrl.split(',');
    const mime = parts[0].split(':')[1].split(';')[0];
    const b64 = parts[1];
    const bytes = atob(b64);
    const arr = new Uint8Array(bytes.length);
    for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
    return new File([new Blob([arr], { type: mime })], name, { type: mime });
  };

  // ── Run workflow ─────────────────────────────────────────────────
  const runWorkflow = async () => {
    if (!workflow)  { addLog('No workflow loaded'); return; }
    if (!targetImage)  { addLog('Select a TARGET image'); return; }
    if (!patternImage) { addLog('Select a PATTERN image'); return; }

    setIsRunning(true);
    setProgress('Uploading images…');
    setProgressPct(10);
    setResultImage(null);

    try {
      const targetUpload = await uploadImage(targetImage, 'LoadImage');
      addLog(`TARGET uploaded: ${targetUpload.name}`);

      const tiledBlob = dataUrlToFile(tiledPreviewUrl, 'tiled_pattern.png');
      const patternUpload = await uploadImage(tiledBlob, 'LoadImage');
      addLog(`PATTERN uploaded: ${patternUpload.name}`);

      const wf = JSON.parse(JSON.stringify(workflow));
      const findNodeIdByTitle = (title) => Object.entries(wf).find(([, node]) => node?._meta?.title === title)?.[0];
      const TARGET_NODE = findNodeIdByTitle('TARGET');
      const PATTERN_NODE = findNodeIdByTitle('PATTERN');
      const OUTPUT_NODE = findNodeIdByTitle('OUTPUT');

      if (!TARGET_NODE || !PATTERN_NODE) {
        throw new Error(`Workflow missing nodes: ${TARGET_NODE || '??'} (TARGET), ${PATTERN_NODE || '??'} (PATTERN)`);
      }

      wf[TARGET_NODE].inputs.image = targetUpload.name;
      wf[PATTERN_NODE].inputs.image = patternUpload.name;
      if (OUTPUT_NODE) addLog(`OUTPUT node: ${OUTPUT_NODE}`);

      for (const node of Object.values(wf)) {
        if (!node.inputs) continue;
        for (const [key, val] of Object.entries(node.inputs)) {
          if (key.toLowerCase().includes('seed') && typeof val === 'number') {
            node.inputs[key] = Math.floor(Math.random() * 1_000_000_000);
          }
        }
      }

      setProgress('Sending to processing engine…');
      setProgressPct(20);
      const result = await executeWorkflow(wf);
      if (!result?.prompt_id) throw new Error('No prompt_id returned');
      addLog(`Prompt ID: ${result.prompt_id}`);
      if (sendMessage) sendMessage({ type: 'subscribe' });
      await pollAndFetch(result.prompt_id);

    } catch (err) {
      addLog(`Error: ${err.message}`);
      setProgress('Failed');
      setIsRunning(false);
    }
  };

  const pollAndFetch = async (promptId) => {
    while (true) {
      try {
        const status = await checkWorkflowStatus(promptId);
        if (status.status === 'completed') {
          addLog(`Status: completed`);
          setProgress('Fetching result…');
          setProgressPct(95);
          await new Promise(r => setTimeout(r, 2000));
          try {
            const data = await fetchWorkflowResults(promptId);
            addLog(`Results keys: ${JSON.stringify(Object.keys(data))}`);
            if (data.outputs) {
              addLog(`Output nodes: ${JSON.stringify(Object.keys(data.outputs))}`);
            }
            const images = data.images || data;
            addLog(`Images array length: ${images?.length ?? 'undefined'}`);
            if (images?.length > 0) {
              addLog(`Got ${images.length} image(s), using node ${images[images.length-1].nodeId}`);
              const outputImg = images[images.length - 1] || images[0];
              const cleanImg = { ...outputImg };
              setResultImage(cleanImg);
              setSessionHistory(prev => [
                { target: targetPreview, pattern: tiledPreviewUrl, result: cleanImg, ts: new Date().toLocaleTimeString() },
                ...prev
              ]);
              setProgress('Complete');
              setProgressPct(100);
              addLog('Result ready!');
            } else {
              addLog('No images in result');
            }
          } catch (fetchErr) {
            addLog(`Fetch results error: ${fetchErr.message}`);
          }
          setIsRunning(false);
          return;
        }
        if (status.status === 'failed') {
          const errMsg = status.errors?.map(e => e.message || e).join('; ') || status.error || 'unknown';
          addLog(`Workflow failed: ${errMsg}`);
          setProgress('Failed');
          setIsRunning(false);
          return;
        }
        if (status.status === 'unknown' || status.status === 'queued') {
          addLog(`Status: ${status.status}${status.debug ? ` (${status.debug})` : ''}`);
        }
        await new Promise(r => setTimeout(r, 1500));
      } catch (e) {
        addLog(`Poll error: ${e.message}`);
        await new Promise(r => setTimeout(r, 1500));
      }
    }
  };

  const downloadResult = () => {
    if (!resultImage?.data) return;
    const a = document.createElement('a');
    a.href = `data:image/png;base64,${resultImage.data}`;
    a.download = `pattern-swap-${Date.now()}.png`;
    a.click();
  };

  return (
    <div className="ps-page">
      <h1 className="ps-title">Pattern Swap</h1>

      <div className="ps-status-bar">
        <span className={`ps-status-dot ${serverStatus}`}>
          {serverStatus === 'connected' ? '● Connected' : '● Disconnected'}
        </span>
        <span className={`ps-ws-dot ${wsConnected ? 'on' : 'off'}`}>
          {wsConnected ? '⚡ WebSocket live' : '⚡ WebSocket off'}
        </span>

      </div>

      <div className="ps-layout">

        {/* LEFT — TARGET column */}
        <div className="ps-col ps-col-target">
          <section
            className={`ps-section ps-target-section${isTargetDragOver ? ' drag-over' : ''}`}
            onDragEnter={handleTargetDragEnter}
            onDragOver={handleTargetDragOver}
            onDragLeave={handleTargetDragLeave}
            onDrop={handleTargetDrop}
          >
            <h2>Target</h2>

            {/* Upload zone or comparison slider */}
            {!resultImage ? (
              <div className="ps-upload-area">
                {targetPreview ? (
                  <ZoomableImage
                    src={targetPreview}
                    alt="Target"
                    showClear
                    onClear={() => { setTargetImage(null); setTargetPreview(null); }}
                  />
                ) : (
                  <label className="ps-dropzone">
                    <span className="ps-dropzone-placeholder">Drop target image</span>
                    <input type="file" accept="image/*" onChange={e => handleTargetFile(e.target.files?.[0])} />
                  </label>
                )}
              </div>
            ) : (
              <div className="ps-result-area">
                <ComparisonSlider
                  beforeSrc={targetPreview}
                  afterSrc={`data:image/png;base64,${resultImage.data}`}
                  beforeLabel="Target"
                  afterLabel="Result"
                />
                <div className="ps-result-actions">
                  <button className="ps-btn ps-btn-download" onClick={downloadResult}>↓ Download</button>
                  <button className="ps-btn ps-btn-reset" onClick={() => setResultImage(null)}>↺ New result</button>
                </div>
              </div>
            )}

            {isTargetDragOver && (
              <div className="ps-target-drop-overlay">Drop to replace target image</div>
            )}
          </section>


          {/* Session history */}
          {sessionHistory.length > 0 && (
            <section className="ps-section">
              <h2>History ({sessionHistory.length})</h2>
              <div className="ps-history-grid">
                {sessionHistory.map((item, i) => (
                  <div key={i} className="ps-history-item" onClick={() => setResultImage(item.result)}>
                    <img src={`data:image/png;base64,${item.result.data}`} alt={`Result ${i}`} className="ps-history-thumb" />
                    <span className="ps-history-ts">{item.ts}</span>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>

        {/* RIGHT — PATTERN column */}
        <div className="ps-col ps-col-pattern">
          <section className="ps-section">
            <h2>Pattern</h2>

            {/* Canvas = preview + dropzone */}
            <div
              ref={patternCanvasWrapRef}
              className="ps-canvas-wrapper" 
              onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
              onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); }}
              onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                const file = e.dataTransfer.files?.[0];
                if (file && file.type.startsWith('image/')) handlePatternFile(file);
              }}
            >
              {!patternImgEl ? (
                <div className="ps-dropzone ps-drop-canvas">
                  <span className="ps-dropzone-placeholder">Drop pattern tile here</span>
                </div>
              ) : (
                <canvas
                  ref={previewCanvasRef}
                  className="ps-interactive-canvas"
                  width={CANVAS_W}
                  height={CANVAS_H}
                  onMouseDown={handlePointerDown}
                  onMouseMove={handlePointerMove}
                  onMouseUp={handlePointerUp}
                  onMouseLeave={handlePointerUp}
                  onTouchStart={handlePointerDown}
                  onTouchMove={handlePointerMove}
                  onTouchEnd={handlePointerUp}
                />
              )}
              <canvas ref={hiddenCanvasRef} style={{ display: 'none' }} />
            </div>

            {patternImage && (
              <button className="ps-btn-sm ps-clear-btn" onClick={() => { setPatternImage(null); setPatternPreview(null); setPatternImgEl(null); setTiledPreviewUrl(null); }}>✕</button>
            )}
          </section>

          {/* Pattern controls */}
          <section className="ps-section">
            <h2>Controls</h2>
            <div className="ps-controls">
              <label>
                <div className="ps-repeat-row">
                  <input
                    className="ps-repeat-input"
                    type="number"
                    min={0.01}
                    max={3}
                    step={0.01}
                    value={repeatCount}
                    onChange={e => {
                      const v = parseFloat(e.target.value);
                      if (!isNaN(v) && v >= 0.01 && v <= 100) setRepeatCount(v);
                    }}
                  />
                  <input
                    className="ps-slider"
                    type="range"
                    min={0.1}
                    max={3}
                    step={0.01}
                    value={repeatCount}
                    onChange={e => setRepeatCount(Number(e.target.value))}
                  />
                </div>
                <div className="ps-slider-labels">
                  <span>0.1×</span>
                  <span>1×</span>
                  <span>2×</span>
                  <span>3×</span>
                </div>
              </label>
              <button
                className="ps-btn-sm ps-reset-btn"
                onClick={() => { setOffsetX(0); setOffsetY(0); }}
              >
                Reset position
              </button>
            </div>

          </section>

          {/* Generate */}
          <section className="ps-section">
            <button
              className="ps-run-btn"
              disabled={isRunning || !targetImage || !patternImage || !workflow}
              onClick={runWorkflow}
            >
              {isRunning ? `Running… ${progressPct}%` : '✨ GENERATE'}
            </button>

            {isRunning && (
              <div className="ps-progress">
                <div className="ps-progress-fill" style={{ width: `${progressPct}%` }} />
                <span className="ps-progress-text">{progress}</span>
              </div>
            )}
          </section>

        </div>
      </div>
    </div>
  );
};

export default PatternSwap;
